import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Container,
    Card,
    CardContent,
    Avatar,
    Divider,
    Alert,
    Button,
    TextField,
    Checkbox,
    FormControlLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
    IconButton
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { getPages, putRequest } from '../../api/axios';
import { useTranslation } from 'react-i18next';

const TemplateManagement = () => {
    const { t } = useTranslation('global');

    const [pages, setPages] = useState([]);
    const [allPages, setAllPages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errMsg, setErrMsg] = useState('');
    const [succ, setSucc] = useState('');
    const [editingPage, setEditingPage] = useState(null);
    const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [selectedPagesForTemplate, setSelectedPagesForTemplate] = useState([]);
    const [newTemplateName, setNewTemplateName] = useState('');

    useEffect(() => {
        fetchAllPages();
    }, []);

    const fetchAllPages = async () => {
        try {
            setLoading(true);
            const response = await getPages();
            setAllPages(response);
            setPages(response);
        } catch (error) {
            setErrMsg('templateManagement.error.fetchPages');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleEditPage = (page) => {
        setEditingPage({ ...page });
    };

    const handleSavePage = async () => {
        if (!editingPage) return;

        try {
            setErrMsg('');
            setSucc('');

            const formData = new URLSearchParams();
            formData.append('page_id', editingPage.id);
            formData.append('templates', editingPage.templates || '');

            const response = await putRequest('/perm/update-page-templates', formData);

            if (response.success) {
                setSucc('templateManagement.success.pageTemplatesUpdated');
                await fetchAllPages();
                setEditingPage(null);
            } else {
                setErrMsg(response.message || 'templateManagement.error.updatePageTemplates');
            }
        } catch (error) {
            setErrMsg('templateManagement.error.updatePageTemplates');
            console.error(error);
        }
    };

    const handleCancelEdit = () => {
        setEditingPage(null);
    };

    const handleOpenTemplateDialog = (templateName = '') => {
        setSelectedTemplate(templateName);
        setNewTemplateName(templateName);

        // Pre-select pages that have this template
        if (templateName) {
            const pagesWithTemplate = pages.filter(page => {
                if (!page.templates) return false;
                const templates = page.templates.split(',').map(t => t.trim());
                return templates.includes(templateName);
            });
            setSelectedPagesForTemplate(pagesWithTemplate.map(p => p.id));
        } else {
            setSelectedPagesForTemplate([]);
        }

        setTemplateDialogOpen(true);
    };

    const handleCloseTemplateDialog = () => {
        setTemplateDialogOpen(false);
        setSelectedTemplate('');
        setNewTemplateName('');
        setSelectedPagesForTemplate([]);
    };

    const handleTogglePageForTemplate = (pageId) => {
        setSelectedPagesForTemplate(prev => {
            if (prev.includes(pageId)) {
                return prev.filter(id => id !== pageId);
            } else {
                return [...prev, pageId];
            }
        });
    };

    const handleSaveTemplate = async () => {
        if (!newTemplateName.trim()) {
            setErrMsg('templateManagement.validation.templateNameRequired');
            return;
        }

        try {
            setErrMsg('');
            setSucc('');

            // Update all selected pages to include this template
            const updatePromises = selectedPagesForTemplate.map(async (pageId) => {
                const page = pages.find(p => p.id === pageId);
                if (!page) return;

                const existingTemplates = page.templates
                    ? page.templates.split(',').map(t => t.trim()).filter(t => t && t !== selectedTemplate)
                    : [];

                // Add new template name
                if (!existingTemplates.includes(newTemplateName.trim())) {
                    existingTemplates.push(newTemplateName.trim());
                }

                const formData = new URLSearchParams();
                formData.append('page_id', pageId);
                formData.append('templates', existingTemplates.join(','));

                return putRequest('/perm/update-page-templates', formData);
            });

            // Remove template from pages that were previously selected but are not now
            if (selectedTemplate) {
                const previouslySelectedPages = pages.filter(page => {
                    if (!page.templates) return false;
                    const templates = page.templates.split(',').map(t => t.trim());
                    return templates.includes(selectedTemplate);
                });

                const pagesToRemoveFrom = previouslySelectedPages.filter(
                    page => !selectedPagesForTemplate.includes(page.id)
                );

                const removePromises = pagesToRemoveFrom.map(async (page) => {
                    const existingTemplates = page.templates
                        ? page.templates.split(',').map(t => t.trim()).filter(t => t && t !== selectedTemplate)
                        : [];

                    const formData = new URLSearchParams();
                    formData.append('page_id', page.id);
                    formData.append('templates', existingTemplates.join(','));

                    return putRequest('/perm/update-page-templates', formData);
                });

                await Promise.all(removePromises);
            }

            await Promise.all(updatePromises);
            setSucc('templateManagement.success.templateSaved');
            await fetchAllPages();
            handleCloseTemplateDialog();
        } catch (error) {
            setErrMsg('templateManagement.error.savingTemplate');
            console.error(error);
        }
    };

    const getAllTemplates = () => {
        const templateSet = new Set();
        pages.forEach(page => {
            if (page.templates) {
                const templates = page.templates.split(',').map(t => t.trim()).filter(t => t);
                templates.forEach(template => {
                    templateSet.add(template);
                });
            }
        });
        return Array.from(templateSet).sort();
    };

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    const templates = getAllTemplates();

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            {/* Header Section */}
            <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar
                        sx={{
                            bgcolor: 'primary.main',
                            width: 56,
                            height: 56,
                        }}
                    >
                        <SettingsIcon sx={{ fontSize: 32 }} />
                    </Avatar>
                    <Box>
                        <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                            {t('user-manuals./template-management.title')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t('user-manuals./template-management.text')}
                        </Typography>
                    </Box>
                </Box>
                <Divider sx={{ my: 3 }} />
            </Box>

            {/* Templates Section */}
            <Card elevation={2} sx={{ mb: 4, borderRadius: 2 }}>
                <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {t('templateManagement.templatesSectionTitle')}
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => handleOpenTemplateDialog()}
                        >
                            {t('templateManagement.buttons.create')}
                        </Button>
                    </Box>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {templates.map((template) => (
                            <Chip
                                key={template}
                                label={template}
                                onClick={() => handleOpenTemplateDialog(template)}
                                sx={{ cursor: 'pointer' }}
                            />
                        ))}
                        {templates.length === 0 && (
                            <Typography variant="body2" color="text.secondary">
                                {t('templateManagement.emptyTemplates')}
                            </Typography>
                        )}
                    </Box>
                </CardContent>
            </Card>

            {/* Pages Table */}
            <Card elevation={2} sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        {t('templateManagement.pagesSectionTitle')}
                    </Typography>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t('templateManagement.table.id')}</TableCell>
                                    <TableCell>{t('templateManagement.table.name')}</TableCell>
                                    <TableCell>{t('templateManagement.table.path')}</TableCell>
                                    <TableCell>{t('templateManagement.table.templates')}</TableCell>
                                    <TableCell>{t('templateManagement.table.actions')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {pages.map((page) => (
                                    <TableRow key={page.id}>
                                        <TableCell>{page.id}</TableCell>
                                        <TableCell>{page.name}</TableCell>
                                        <TableCell>{page.path}</TableCell>
                                        <TableCell>
                                            {editingPage?.id === page.id ? (
                                                <TextField
                                                    size="small"
                                                    value={editingPage.templates || ''}
                                                    onChange={(e) => setEditingPage({ ...editingPage, templates: e.target.value })}
                                                    placeholder={t('templateManagement.table.placeholderTemplates')}
                                                    fullWidth
                                                />
                                            ) : (
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {page.templates ? (
                                                        page.templates.split(',').map((t, idx) => (
                                                            <Chip key={idx} label={t.trim()} size="small" />
                                                        ))
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">
                                                            {t('templateManagement.table.noTemplates')}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {editingPage?.id === page.id ? (
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={handleSavePage}
                                                    >
                                                        <SaveIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        onClick={handleCancelEdit}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Box>
                                            ) : (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleEditPage(page)}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>

            {/* Template Dialog */}
            <Dialog
                open={templateDialogOpen}
                onClose={handleCloseTemplateDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    {selectedTemplate
                        ? t('templateManagement.dialog.editTitle', { name: selectedTemplate })
                        : t('templateManagement.dialog.createTitle')}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label={t('templateManagement.dialog.templateNameLabel')}
                        value={newTemplateName}
                        onChange={(e) => setNewTemplateName(e.target.value)}
                        margin="normal"
                        disabled={!!selectedTemplate}
                    />
                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                        {t('templateManagement.dialog.selectPagesLabel')}
                    </Typography>
                    <Box sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                        <List dense>
                            {allPages.map((page) => (
                                <ListItem key={page.id}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={selectedPagesForTemplate.includes(page.id)}
                                                onChange={() => handleTogglePageForTemplate(page.id)}
                                            />
                                        }
                                        label={`${page.name} (${page.path})`}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseTemplateDialog}>
                        {t('templateManagement.buttons.cancel')}
                    </Button>
                    <Button onClick={handleSaveTemplate} variant="contained">
                        {t('templateManagement.buttons.save')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Error and Success Messages */}
            <Box sx={{ mt: 3 }}>
                {errMsg && (
                    <Alert
                        severity="error"
                        onClose={() => setErrMsg('')}
                        sx={{ mb: 2, borderRadius: 2 }}
                    >
                        {t(errMsg)}
                    </Alert>
                )}
                {succ && (
                    <Alert
                        severity="success"
                        onClose={() => setSucc('')}
                        sx={{ mb: 2, borderRadius: 2 }}
                    >
                        {t(succ)}
                    </Alert>
                )}
            </Box>
        </Container>
    );
};

export default TemplateManagement;

